import React from 'react';
import invariant from 'invariant';
import { setDisplayName, wrapDisplayName, type HOC } from 'recompose';
import type { ApplicationType } from '../flowTypes';
import initializeGlobalize from './initializeGlobalize';
import ComponentLoader from '../../components/ComponentLoader';
import changeLocale from './changeLocale';
import messages from '../../messages';

type RequiredProps = {
  settings?: any,
  application: ApplicationType,
};
type State = {
  ready: boolean,
  errorMessage: ?string,
  preferredLanguage: string,
};

const withGlobalize = (): HOC<*, RequiredProps> => (BaseComponent) => {
  class WithGlobalize extends React.Component<RequiredProps, State> {
    state = {
      ready: false,
      errorMessage: null,
      preferredLanguage: 'en',
    };

    static getDerivedStateFromProps(nextProps, prevState) {
      if (prevState.ready) {
        const { preferredLanguage, supportedLaguages } = WithGlobalize.getLangs(nextProps);

        if (prevState.preferredLanguage !== preferredLanguage) {
          changeLocale(preferredLanguage, supportedLaguages);
          return { preferredLanguage };
        }
      }
      // Return null to indicate no change to state.
      return null;
    }

    componentDidMount() {
      const { preferredLanguage, supportedLaguages } = WithGlobalize.getLangs(this.props);
      initializeGlobalize(preferredLanguage, supportedLaguages, messages(preferredLanguage))
        .then(() => {
          this.setState({ ready: true, preferredLanguage });
        })
        .catch((err: Error) => {
          this.setState({ errorMessage: err.message });
        });
    }
    static getLangs = (props: RequiredProps) => {
      const { settings, application } = props;
      let preferredLanguage, supportedLaguages;

      const language = application.language;
      const supported = null;
      //const supported =  Application.get('supportedLaguages');

      if (language && !supported) {
        preferredLanguage = language;
        supportedLaguages = [language];
      } else if (language && supported) {
        preferredLanguage = language;
        supportedLaguages = supported;
      } else if (settings) {
        preferredLanguage = settings.preferredLanguage;
        supportedLaguages = settings.supportedLanguages.map((l) => l.code);
      } else {
        invariant(
          false,
          'Please provide application language as Application property `language` or as server side settings.',
        );
      }

      return { preferredLanguage, supportedLaguages };
    };

    render() {
      const { errorMessage, ready } = this.state;

      if (errorMessage) {
        console.error(errorMessage);
        return <div>{errorMessage}</div>;
      }

      return ready ? <BaseComponent {...this.props} /> : <ComponentLoader loading />;
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    return setDisplayName(wrapDisplayName(BaseComponent, 'withGlobalize'))(WithGlobalize);
  }

  return WithGlobalize;
};

export default withGlobalize;



// WEBPACK FOOTER //
// ./src/application/globalize/withGlobalize.js